// AGC: Subscription-tier capabilities - orthogonal to RBAC
// AGC: RBAC answers "given your role, can you do X?"; this answers "given your plan, is feature Y unlocked?"
// AGC: Prefer this over usePermissions.canAddSubmanagers / canAccessAdvisor for new code

import { useMemo } from 'react'
import { useSubscription } from '../context/SubscriptionContext'
import { useRBAC } from './useRBAC'

export interface SubscriptionCapabilities {
  // AGC: Existing — kept unchanged
  canUseAdvisor: boolean
  canAddTeamMembers: boolean
  canAddSubmanagers: boolean
  memberLimit: number | null

  // AGC: WhatsApp tier gating (new)
  whatsappAgentEnabled: boolean
  whatsappAudioCap: number | null

  // AGC: Voice-app tier gating (new)
  voiceSotaEnabled: boolean
  voiceAudioCap: number | null
  voiceAgentEnabled: boolean
  voiceAgentDefaultMode: 'A' | 'B'

  isLoading: boolean
}

/**
 * AGC: Get subscription-tier-based capabilities for the current user
 */
export const useSubscriptionCapabilities = (): SubscriptionCapabilities => {
  const { tier, isLoading: subLoading } = useSubscription()
  const rbac = useRBAC()

  return useMemo(() => {
    if (subLoading || rbac.isLoading) {
      return {
        canUseAdvisor: false,
        canAddTeamMembers: false,
        canAddSubmanagers: false,
        memberLimit: 0,
        whatsappAgentEnabled: false,
        whatsappAudioCap: 30,
        voiceSotaEnabled: false,
        voiceAudioCap: 30,
        voiceAgentEnabled: false,
        voiceAgentDefaultMode: 'A',
        isLoading: true,
      }
    }

    const canManageTeamRole = rbac.isOwner || rbac.isManager

    if (tier === 'free') {
      return {
        canUseAdvisor: false,
        canAddTeamMembers: false,
        canAddSubmanagers: false,
        memberLimit: 1,
        whatsappAgentEnabled: true,
        whatsappAudioCap: 30,
        voiceSotaEnabled: true,
        voiceAudioCap: 30,
        voiceAgentEnabled: true,
        voiceAgentDefaultMode: 'A',
        isLoading: false,
      }
    }

    if (tier === 'pro') {
      return {
        canUseAdvisor: true,
        canAddTeamMembers: canManageTeamRole,
        canAddSubmanagers: false,
        memberLimit: 5,
        whatsappAgentEnabled: true,
        whatsappAudioCap: null,
        voiceSotaEnabled: true,
        voiceAudioCap: null,
        voiceAgentEnabled: true,
        voiceAgentDefaultMode: 'A',
        isLoading: false,
      }
    }

    // AGC: premium
    return {
      canUseAdvisor: true,
      canAddTeamMembers: canManageTeamRole,
      canAddSubmanagers: canManageTeamRole,
      memberLimit: null,
      whatsappAgentEnabled: true,
      whatsappAudioCap: null,
      voiceSotaEnabled: true,
      voiceAudioCap: null,
      voiceAgentEnabled: true,
      voiceAgentDefaultMode: 'A',
      isLoading: false,
    }
  }, [tier, subLoading, rbac])
}
